import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { OrganicShapes } from '../components/decor/OrganicShapes';
import { Reveal } from '../components/Reveal';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'July 21, 2026';

export function TermsOfServicePage() {
  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#f9fbfc] via-white to-[#F4F7F9] py-16 lg:py-20">
        <OrganicShapes variant="hero" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-6xl text-[#1f2937] mb-6 leading-tight">Terms of Service</h1>
          <p className="text-lg text-[#4B5563]">Last updated: {LAST_UPDATED}</p>
        </Reveal>
      </section>

      {/* Body */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-[#4B5563] leading-relaxed">
            <div className="space-y-4">
              <p>
                These Terms of Service (&ldquo;Terms&rdquo;) govern your access to and use of the
                website (the &ldquo;Site&rdquo;) operated by PleromaHealth LLC (&ldquo;PleromaHealth,&rdquo;
                &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By accessing or using the
                Site, you agree to be bound by these Terms. If you do not agree to these Terms, please
                do not use the Site.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">1. Use of the Site</h2>
              <p>
                The Site is provided for general informational purposes about PleromaHealth and its
                products and services. You may use the Site only for lawful purposes and in accordance
                with these Terms. You agree not to use the Site in any way that could damage, disable,
                overburden, or impair the Site, or interfere with any other party&rsquo;s use of it, or
                attempt to gain unauthorized access to any systems or networks connected to the Site.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">2. No Professional, Medical, or Legal Advice</h2>
              <p>
                Content on the Site is provided for informational purposes only and does not constitute
                medical, clinical, legal, financial, or other professional advice. The Site is not
                intended for the diagnosis or treatment of any medical condition and is not a
                substitute for the advice of a qualified professional. Nothing on the Site creates a
                provider-patient relationship. You should not rely on the Site as the sole basis for
                any decision.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">3. Intellectual Property</h2>
              <p>
                The Site and its contents&mdash;including text, graphics, logos, images, and
                software&mdash;are owned by or licensed to PleromaHealth and are protected by
                copyright, trademark, and other intellectual property laws. You may view and download
                content from the Site for your own informational, non-commercial use, provided you do
                not remove any proprietary notices. You may not otherwise reproduce, distribute,
                modify, or create derivative works from the Site without our prior written permission.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">4. Trademarks</h2>
              <p>
                &ldquo;PleromaHealth,&rdquo; &ldquo;HealthBridge,&rdquo; and our logos are trademarks
                of PleromaHealth LLC. All other product and company names referenced on the Site may be
                trademarks of their respective owners and are used for identification purposes only.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">5. Privacy</h2>
              <p>
                Your use of the Site is also governed by our{' '}
                <Link to="/privacy-policy" className="text-[#34AFFB] hover:underline">Privacy Policy</Link>,
                which is incorporated into these Terms by reference. As described there, this Site does
                not collect or process personal information and gathers only aggregate website
                statistics.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">6. Third-Party Links</h2>
              <p>
                The Site may contain links to third-party websites and resources that we do not control.
                We provide these links for convenience only and are not responsible for the content,
                products, or services available from those sites. Your use of any linked third-party
                site is at your own risk and subject to that site&rsquo;s terms and policies.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">7. Disclaimer of Warranties</h2>
              <p>
                The Site is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis
                without warranties of any kind, whether express or implied, including but not limited to
                implied warranties of merchantability, fitness for a particular purpose, title, and
                non-infringement. We do not warrant that the Site will be uninterrupted, error-free, or
                free of harmful components, or that the content will be accurate, complete, or current.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">8. Limitation of Liability</h2>
              <p>
                To the fullest extent permitted by law, PleromaHealth and its affiliates, officers,
                employees, and agents will not be liable for any indirect, incidental, special,
                consequential, or punitive damages, or any loss of profits or revenues, arising out of
                or in connection with your access to or use of, or inability to use, the Site, whether
                based on warranty, contract, tort, or any other legal theory, even if we have been
                advised of the possibility of such damages.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">9. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless PleromaHealth and its affiliates, officers,
                employees, and agents from and against any claims, liabilities, damages, losses, and
                expenses, including reasonable attorneys&rsquo; fees, arising out of or in any way
                connected with your use of the Site or your violation of these Terms.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">10. Changes to the Site and Terms</h2>
              <p>
                We may modify or discontinue the Site, or any part of it, at any time without notice. We
                may also revise these Terms from time to time. When we do, we will update the
                &ldquo;Last updated&rdquo; date above. Your continued use of the Site after any changes
                take effect constitutes your acceptance of the revised Terms.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">11. Governing Law</h2>
              <p>
                These Terms are governed by and construed in accordance with the laws of the State of
                Texas, without regard to its conflict-of-laws principles. You agree that any dispute
                arising out of or relating to these Terms or the Site will be subject to the exclusive
                jurisdiction of the state and federal courts located in Texas.
              </p>
            </div>

            <div className="space-y-4">
              <h2 className="text-2xl text-[#1f2937] font-semibold">12. Contact Us</h2>
              <p>
                If you have questions about these Terms, please contact us at{' '}
                <a href="mailto:info@pleromahealth.com" className="text-[#34AFFB] hover:underline">
                  info@pleromahealth.com
                </a>
                .
              </p>
              <p>PleromaHealth LLC · Austin, TX and Dallas, TX</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
